import { useTranslation } from 'react-i18next';

import type { PhotoVideoConsent } from '../../services/consentApi';

interface ConsentSectionProps {
  photoVideoConsent: PhotoVideoConsent | null;
  waiverAgreed: boolean;
  releaseAgreed: boolean;
  isAdultOnly?: boolean;
  onPhotoConsentChange: (value: PhotoVideoConsent) => void;
  onWaiverChange: (checked: boolean) => void;
  onReleaseChange: (checked: boolean) => void;
}

export default function ConsentSection({
  photoVideoConsent,
  waiverAgreed,
  releaseAgreed,
  isAdultOnly,
  onPhotoConsentChange,
  onWaiverChange,
  onReleaseChange,
}: ConsentSectionProps) {
  const { t } = useTranslation(['booking']);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {t('booking:consent.section.title')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">{t('booking:consent.section.subtitle')}</p>
      </div>

      {/* Photo & Video Consent */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h3 className="font-medium text-gray-900 mb-3">{t('booking:consent.photoVideo.title')}</h3>
        <p className="text-sm text-gray-600 mb-4">{t('booking:consent.photoVideo.description')}</p>

        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="photoVideoConsent"
              value="FULL"
              checked={photoVideoConsent === 'FULL'}
              onChange={() => onPhotoConsentChange('FULL')}
              className="mt-0.5"
            />
            <span className="text-sm text-gray-700">
              {t('booking:consent.photoVideo.optionFull')}
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="photoVideoConsent"
              value="BLURRED"
              checked={photoVideoConsent === 'BLURRED'}
              onChange={() => onPhotoConsentChange('BLURRED')}
              className="mt-0.5"
            />
            <span className="text-sm text-gray-700">
              {t('booking:consent.photoVideo.optionBlurred')}
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="photoVideoConsent"
              value="NO_PHOTO"
              checked={photoVideoConsent === 'NO_PHOTO'}
              onChange={() => onPhotoConsentChange('NO_PHOTO')}
              className="mt-0.5"
            />
            <span className="text-sm text-gray-700">
              {t('booking:consent.photoVideo.optionNo')}
            </span>
          </label>
        </div>
      </div>

      {/* Liability Waiver */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-3">{t('booking:consent.waiver.title')}</h3>
        <div className="text-sm text-gray-600 mb-4 max-h-32 overflow-y-auto bg-gray-50 p-3 rounded">
          {t('booking:consent.waiver.content')}
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={waiverAgreed}
            onChange={(e) => onWaiverChange(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-sm text-gray-700">{t('booking:consent.waiver.agree')}</span>
        </label>
      </div>

      {/* Parental Release - hidden for adult-only activities */}
      {!isAdultOnly && (
        <div className="p-4 border border-gray-200 rounded-lg">
          <h3 className="font-medium text-gray-900 mb-3">{t('booking:consent.release.title')}</h3>
          <div className="text-sm text-gray-600 mb-4 max-h-32 overflow-y-auto bg-gray-50 p-3 rounded">
            {t('booking:consent.release.content')}
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={releaseAgreed}
              onChange={(e) => onReleaseChange(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-sm text-gray-700">{t('booking:consent.release.agree')}</span>
          </label>
        </div>
      )}
    </div>
  );
}
